import { useEffect, useState } from "react";
import { api, API_BASE, fmtMoney, fmtPct } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BOOKS = [
  { title: "Market Wizards", author: "Jack Schwager", quote: "Discipline is the bridge between goals and accomplishment." },
  { title: "Reminiscences of a Stock Operator", author: "Edwin Lefèvre", quote: "It never was my thinking that made the big money for me. It always was my sitting." },
  { title: "Trading in the Zone", author: "Mark Douglas", quote: "Anything can happen. You don't need to know what's next to make money." },
  { title: "One Up on Wall Street", author: "Peter Lynch", quote: "Know what you own, and know why you own it." },
  { title: "The Intelligent Investor", author: "Benjamin Graham", quote: "The investor's chief problem — and even his worst enemy — is likely to be himself." },
  { title: "Technical Analysis of the Financial Markets", author: "John Murphy", quote: "The trend is your friend, until the end when it bends." },
];

export default function AIInsights() {
  const [crypto, setCrypto] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.allSettled([api.get("/markets/crypto"), api.get("/markets/stocks")]);
      if (c.status === "fulfilled") setCrypto(c.value.data.slice(0, 6));
      if (s.status === "fulfilled") setStocks(s.value.data.slice(0, 6));
    })();
  }, []);

  const ask = async (asset) => {
    setSelected(asset);
    setLoading(true);
    setInsight("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/ai/insights`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ symbol: asset.symbol, asset_type: asset.asset_type, price: asset.price, change_pct: asset.change_pct }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() || "";
        for (const c of chunks) {
          if (c.startsWith("data: ")) {
            const t = c.slice(6);
            if (t === "[DONE]") continue;
            if (t.startsWith("[ERROR")) { toast.error("AI error"); continue; }
            setInsight((prev) => prev + t);
          }
        }
      }
    } catch {
      toast.error("Request failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--ai))]"><Sparkles className="w-3 h-3" /> AI Coach</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Wisdom from the classics</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Get analysis on any asset, grounded in principles from Market Wizards, Reminiscences, Trading in the Zone, and more.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Popular crypto</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {crypto.map((a) => (
                <button key={a.symbol} onClick={() => ask(a)} data-testid={`ai-ask-${a.symbol}`}
                        className="text-left p-3 rounded-lg border border-border hover:border-[hsl(var(--ai))] transition-colors">
                  <div className="font-medium text-sm">{a.ticker}</div>
                  <div className={`font-mono-tab text-xs ${a.change_pct >= 0 ? "gain" : "loss"}`}>{fmtPct(a.change_pct)}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Popular stocks</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {stocks.map((a) => (
                <button key={a.symbol} onClick={() => ask(a)} data-testid={`ai-ask-${a.symbol}`}
                        className="text-left p-3 rounded-lg border border-border hover:border-[hsl(var(--ai))] transition-colors">
                  <div className="font-medium text-sm">{a.ticker}</div>
                  <div className={`font-mono-tab text-xs ${a.change_pct >= 0 ? "gain" : "loss"}`}>{fmtPct(a.change_pct)}</div>
                </button>
              ))}
            </div>
          </div>

          <Card className="p-6 rounded-xl ai-tint min-h-[280px]" data-testid="ai-panel">
            {selected ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
                  <div className="font-display font-semibold">{selected.ticker} — {selected.name}</div>
                  <span className={`font-mono-tab text-xs ${selected.change_pct >= 0 ? "gain" : "loss"}`}>{fmtPct(selected.change_pct)}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap" data-testid="ai-output">
                  {insight}
                  {loading && <span className="inline-flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Consulting the classics...</span>}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-10">
                Select an asset above to receive an AI analysis grounded in classic trading books.
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">The library</div>
          {BOOKS.map((b) => (
            <Card key={b.title} className="p-4 border-border rounded-xl" data-testid={`book-${b.title.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="font-display font-semibold text-sm">{b.title}</div>
              <div className="text-xs text-muted-foreground">{b.author}</div>
              <div className="text-xs italic text-muted-foreground mt-2">"{b.quote}"</div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
